import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import moment from 'moment-timezone'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormLabel,
  FormControl,
} from '@mui/material'
import { Clear as ClearIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import 'date-fns'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DatePicker from '@mui/lab/DatePicker'
import { RootState } from '../../reducers'
import { fetchUserProfileData } from '../../actions'
import { IFormInputs } from 'interfaces/user'
import { useStyles } from './UserProfileStyles'

export function UserProfileView({ user, role, handleClose, profileOpen, setProfileOpen }): JSX.Element {
  const classes = useStyles()
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date())
  const dispatch = useDispatch()
  const { data } = useSelector((state: RootState) => state.getUserProfile)
  const [overridePermitted, setOverridePermitted] = useState(false)
  const [qcAllowed, setQcAllowed] = useState(false)
  const [priorityAllowed, setPriorityAllowed] = useState(false)
  const [mcAllowed, setMcAllowed] = useState(false)
  const [multipleClassifiersAllowed, setMultipleClassifiersAllowed] = useState(false)
  const [processCodes, setProcessCodes] = useState<string[]>([])
  const { procedure_codes, states } = useSelector((state: RootState) => state.appConfig)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userProData = data ? data[0] : {}
  const isShow = role === 'NL_Pathologist' ? true : false
  useEffect(() => {
    dispatch(fetchUserProfileData(user))
    setProcessCodes(procedure_codes)
  }, [dispatch])

  const { register, setValue, handleSubmit } = useForm<IFormInputs>({
    mode: 'onChange',
  })

  useEffect(() => {
    if (userProData) {
      setValue('first_name', userProData.first_name)
      setValue('last_name', userProData.last_name)
      setValue('credential', userProData.credential)
      setValue('email_id', userProData.email_id)
      setValue('phone_number', userProData.phone_number)
      setValue('user_status', userProData.user_status)
      setValue('any_state_override', userProData.any_state_override)
      setValue('max_cases_per_day', userProData.max_cases_per_day)
      setValue('qc_allowed', userProData.qc_allowed)
      setValue('priority_allowed', userProData.priority_allowed)
      setValue('mc_allowed', userProData.mc_allowed)
      setValue('multiple_classifiers_allowed', userProData.multiple_classifiers_allowed)
      setOverridePermitted(userProData.any_state_override)
      setQcAllowed(userProData.qc_allowed)
      setPriorityAllowed(userProData.priority_allowed)
      setMcAllowed(userProData.mc_allowed)
      setMultipleClassifiersAllowed(userProData.multiple_classifiers_allowed)
    }
  }, [data, setValue, userProData])

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date)
    const dateStr = moment.tz(date, moment.locale()).format('YYYY-MM-DD').toString()
    setValue('expiry_date', dateStr)
  }

  const onCodeChange = (val) => {
    setValue('procedure_code_id', val)
  }

  const dataProcedurecode = (val) => {
    const filterProcedureCodes = procedure_codes.filter((value) => {
      return value.id == val
    })
    if (val == '') {
      return ''
    } else {
      return filterProcedureCodes[0]?.cdx_procedure_code + ' - ' + filterProcedureCodes[0]?.description
    }
  }

  const onStateChange = (val) => {
    setValue('state_code', val)
  }

  const onSubmit = (data: IFormInputs) => {
    setProfileOpen(false)
  }

  const onCancel = (e) => {
    handleClose(e)
  }

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog
          open={profileOpen}
          maxWidth={'md'}
          scroll={'paper'}
          fullWidth
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <DialogTitle id="scroll-dialog-title" className={classes.dialogTitle}>
            My Profile
          </DialogTitle>
          <DialogContent dividers={true}>
            <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
              <Box sx={{ flexGrow: 1 }}>
                <h3 className={classes.lableViewMrg10}>Contact Information</h3>
                <Grid container spacing={2} className={classes.mT20}>
                  <Grid item xs={4}>
                    <TextField
                      inputProps={{
                        style: {
                          padding: 10,
                        },
                      }}
                      id="outlined-basic"
                      label="First Name"
                      {...register('first_name')}
                      variant="outlined"
                      disabled={true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      inputProps={{
                        style: {
                          padding: 10,
                        },
                      }}
                      id="outlined-basic"
                      label="Last Name"
                      {...register('last_name')}
                      variant="outlined"
                      disabled={true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      inputProps={{
                        style: {
                          padding: 10,
                        },
                      }}
                      id="outlined-basic"
                      label="Credentials (ex. M.D.)"
                      {...register('credential')}
                      variant="outlined"
                      disabled={true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} className={classes.mT20}>
                  <Grid item xs={4}>
                    <TextField
                      inputProps={{
                        style: {
                          padding: 10,
                        },
                      }}
                      InputLabelProps={{ shrink: true }}
                      id="outlined-basic"
                      label="Email Address"
                      {...register('email_id')}
                      variant="outlined"
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      inputProps={{
                        style: {
                          padding: 10,
                        },
                      }}
                      id="outlined-basic"
                      label="Phone"
                      {...register('phone_number')}
                      variant="outlined"
                      disabled={true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                {isShow && (
                  <>
                    <h3>State Licenses</h3>
                    <Grid item xs={12}>
                      <TextField
                        inputProps={{
                          style: {
                            padding: 10,
                          },
                        }}
                        id="outlined-basic"
                        label="Account Status"
                        {...register('user_status')}
                        variant="outlined"
                        disabled={true}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {userProData &&
                      userProData?.user_licenses?.map((val, i) => (
                        // eslint-disable-next-line react/jsx-key
                        <Grid key={i} container spacing={2} className={classes.mT20}>
                          <Grid item xs={4}>
                            <Autocomplete
                              id="combo-box-demo"
                              value={val.state_code}
                              options={states}
                              onChange={(_e, val) => onStateChange(val)}
                              disabled={true}
                              renderInput={(params) => (
                                <TextField {...params} label="State/Locale" variant="outlined" size="small" />
                              )}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={val.expiry_date}
                                label="Expires"
                                onChange={handleDateChange}
                                InputProps={{
                                  style: {
                                    height: 40,
                                  },
                                }}
                                disabled
                                renderInput={(params) => <TextField {...params} />}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      ))}
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          value={true}
                          control={<Checkbox color="primary" />}
                          {...register('any_state_override')}
                          label="User can perform state overides"
                          checked={overridePermitted}
                          labelPlacement="end"
                          disabled={true}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                {isShow && (
                  <>
                    <h3 className={classes.lableViewMrg}>Caseload Settings</h3>
                    <Grid container spacing={2} className={classes.mT10}>
                      <Grid item xs={12}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          id="outlined-basic"
                          label="Max cases per day"
                          {...register('max_cases_per_day')}
                          variant="outlined"
                          disabled={true}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <FormControl component="fieldset" className={`${classes.fieldset}`}>
                      <FormLabel style={{ fontSize: '15px', fontWeight: 'bold' }}>Procedure Codes</FormLabel>
                      <FormGroup row>
                        {userProData &&
                          userProData?.user_qc_settings?.map((val, i) => (
                            // eslint-disable-next-line react/jsx-key
                            <Grid key={i} container spacing={2} className={classes.mT10}>
                              <Grid item xs={6}>
                                <Autocomplete
                                  id="combo-box-demo"
                                  options={processCodes}
                                  value={dataProcedurecode(val?.procedure_code_id)}
                                  onChange={(e, val) => onCodeChange(val)}
                                  disabled={true}
                                  renderInput={(params) => (
                                    <TextField {...params} label="Procedure Code" variant="outlined" size="small" />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  inputProps={{
                                    style: {
                                      padding: 10,
                                    },
                                  }}
                                  id="outlined-basic"
                                  label="QC allocation percentage"
                                  {...register('qc_allowed')}
                                  variant="outlined"
                                  value={val.qc_percentage}
                                  disabled={true}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>
                          ))}
                      </FormGroup>
                    </FormControl>
                  </>
                )}

                {isShow && (
                  <>
                    <Grid container spacing={2} className={classes.mp0}>
                      <h3 className={classes.lableViewMrg}>Qualifications</h3>
                      <Grid item xs={12} className={`${classes.mpTB0}`}>
                        <FormControlLabel
                          value={true}
                          control={<Checkbox color="primary" />}
                          {...register('qc_allowed')}
                          label="User is qualified to perform QC."
                          labelPlacement="end"
                          className={classes.dBlock}
                          checked={qcAllowed}
                          disabled={true}
                        />
                        <FormControlLabel
                          value={true}
                          control={<Checkbox color="primary" />}
                          {...register('priority_allowed')}
                          label="User is qualified to accept Priority and/or Hand Hold cases."
                          labelPlacement="end"
                          className={classes.dBlock}
                          checked={priorityAllowed}
                          disabled={true}
                        />
                        <FormControlLabel
                          value={true}
                          control={<Checkbox color="primary" />}
                          {...register('mc_allowed')}
                          label="User is qualified to process Medicare cases"
                          labelPlacement="end"
                          className={classes.dBlock}
                          checked={mcAllowed}
                          disabled={true}
                        />
                        <FormControlLabel
                          value={true}
                          control={<Checkbox color="primary" />}
                          {...register('multiple_classifiers_allowed')}
                          label="User is qualified to view multiple classifiers"
                          labelPlacement="end"
                          className={classes.dBlock}
                          checked={multipleClassifiersAllowed}
                          disabled={true}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions className={classes.userProfileBtn}>
            <Button variant="outlined" onClick={(e) => onCancel(e)} endIcon={<ClearIcon />}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </React.Fragment>
  )
}
